import React, { useEffect, useState } from "react";
import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Activity, Users, Clock, MapPin, Zap } from "lucide-react";
import DashboardWidget from "@/components/DashboardWidget";

interface AnalyticsStats {
  realTimeVisitors: number;
  sessionsToday: number;
  sessionsThisWeek: number;
  sessionsThisMonth: number;
  locationStats: Array<{ location: string; count: number }>;
}

interface RealTimeActivityWidgetProps {
  stats: AnalyticsStats | null;
}

export const RealTimeActivityWidget: React.FC<RealTimeActivityWidgetProps> = ({
  stats,
}) => {
  const [pulseClass, setPulseClass] = useState("");

  // Add pulse animation when real-time visitors change
  useEffect(() => {
    if (stats?.realTimeVisitors && stats.realTimeVisitors > 0) {
      setPulseClass("animate-pulse");
      const timer = setTimeout(() => setPulseClass(""), 2000);
      return () => clearTimeout(timer);
    }
  }, [stats?.realTimeVisitors]);

  const getActivityLevel = () => {
    const visitors = stats?.realTimeVisitors || 0;
    if (visitors >= 20)
      return { level: "High", color: "text-red-600", bgColor: "bg-red-50" };
    if (visitors >= 10)
      return {
        level: "Medium",
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
      };
    if (visitors >= 1)
      return { level: "Low", color: "text-green-600", bgColor: "bg-green-50" };
    return { level: "None", color: "text-gray-600", bgColor: "bg-gray-50" };
  };

  const activityLevel = getActivityLevel();

  const getGrowthIndicator = (current: number, previous: number) => {
    if (previous === 0)
      return { icon: "📈", text: "New", color: "text-blue-600" };
    const growth = ((current - previous) / previous) * 100;
    if (growth > 10)
      return {
        icon: "🚀",
        text: `+${growth.toFixed(0)}%`,
        color: "text-green-600",
      };
    if (growth > 0)
      return {
        icon: "📈",
        text: `+${growth.toFixed(0)}%`,
        color: "text-green-600",
      };
    if (growth < -10)
      return {
        icon: "📉",
        text: `${growth.toFixed(0)}%`,
        color: "text-red-600",
      };
    return {
      icon: "➡️",
      text: `${growth.toFixed(0)}%`,
      color: "text-gray-600",
    };
  };

  return (
    <DashboardWidget
      id="real-time-activity"
      title="Real-Time Activity"
      headerActions={<Activity className="h-5 w-5 text-green-600" />}
    >
      <div className="space-y-4">
        {/* Real-time visitors indicator */}
        <div
          className={`p-4 rounded-lg border-2 ${activityLevel.bgColor} border-current ${activityLevel.color}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Activity
                className={`h-5 w-5 ${activityLevel.color} ${pulseClass}`}
              />
              <span className="font-semibold">Live Visitors</span>
            </div>
            <Badge variant="outline" className={activityLevel.color}>
              {activityLevel.level} Activity
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <div className={`text-3xl font-bold ${activityLevel.color}`}>
              {stats?.realTimeVisitors || 0}
            </div>
            <div className="text-sm text-gray-600">
              Currently browsing your site
            </div>
          </div>
        </div>

        {/* Activity summary */}
        <div className="grid grid-cols-1 gap-3">
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-blue-900">Today</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-blue-900">
                {stats?.sessionsToday || 0}
              </span>
              <span className="text-xs text-blue-600">sessions</span>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-green-600" />
              <span className="font-medium text-green-900">This Week</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-green-900">
                {stats?.sessionsThisWeek || 0}
              </span>
              {stats?.sessionsToday && (
                <span
                  className={`text-xs ${getGrowthIndicator(stats.sessionsThisWeek || 0, (stats.sessionsThisWeek || 0) - (stats.sessionsToday || 0)).color}`}
                >
                  {
                    getGrowthIndicator(
                      stats.sessionsThisWeek,
                      (stats.sessionsThisWeek || 0) -
                        (stats.sessionsToday || 0),
                    ).icon
                  }
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-purple-600" />
              <span className="font-medium text-purple-900">This Month</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-purple-900">
                {stats?.sessionsThisMonth || 0}
              </span>
              {stats?.sessionsThisWeek && (
                <span
                  className={`text-xs ${getGrowthIndicator(stats.sessionsThisMonth || 0, (stats.sessionsThisMonth || 0) - (stats.sessionsThisWeek || 0)).color}`}
                >
                  {
                    getGrowthIndicator(
                      stats.sessionsThisMonth,
                      (stats.sessionsThisMonth || 0) -
                        (stats.sessionsThisWeek || 0),
                    ).icon
                  }
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Top active locations */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Most Active Locations
          </h4>
          <div className="space-y-2">
            {stats?.locationStats?.slice(0, 3).map((location, index) => (
              <div
                key={location.location}
                className="flex items-center justify-between p-2 bg-gray-50 rounded"
              >
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 bg-gray-600 text-white rounded-full text-xs font-bold">
                    {index + 1}
                  </span>
                  <span className="text-sm font-medium">
                    {location.location}
                  </span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {location.count}
                </Badge>
              </div>
            )) || (
              <div className="text-sm text-gray-500 text-center py-2">
                No location data available
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardWidget>
  );
};

export default RealTimeActivityWidget;
